/**
 * The bottom bar: the camera and the three tabs, in one fixed piece.
 *
 * They used to be two separately-positioned elements, which meant the camera
 * floated over whatever row happened to be under it and the list slid beneath
 * it as you scrolled. Now the bar is one opaque surface that the page scrolls
 * behind, so the camera sits *in* the app rather than on top of it — and one
 * fixed element can't drift out of step with another on a phone browser whose
 * toolbar keeps resizing the viewport.
 *
 * The two ways to add only appear where adding makes sense. When they're absent
 * the bar shrinks to just the tabs, so no empty space is reserved for buttons
 * that aren't there.
 */

import { CameraButton } from "./CameraButton";
import { Icon } from "./Icon";
import { NavBar } from "./NavBar";
import type { Tab } from "./NavBar";

import "./Dock.css";

interface DockProps {
  tab: Tab;
  onSelect: (tab: Tab) => void;
  /** Omitted where adding doesn't belong, or while a sheet is open. */
  adding?: {
    onCapture: (photo: string) => void;
    onError: (message: string) => void;
    /** Opens the category sheet — the manual way in. */
    onPick: () => void;
  };
}

export function Dock({ tab, onSelect, adding }: DockProps) {
  return (
    <div className="dock">
      {/* A short fade so content dissolves into the bar instead of being
          guillotined by its top edge. Purely functional: it's the only hint
          that there's more list below. */}
      <div className="dock__fade" aria-hidden="true" />

      {adding && (
        <div className="dock__add">
          <CameraButton onCapture={adding.onCapture} onError={adding.onError} />
          {/* Secondary to the camera, and it looks it: the camera is the one
              this app is actually for. */}
          <button
            className="dock__manual"
            type="button"
            aria-label="Add by hand"
            onClick={adding.onPick}
          >
            <Icon name="plus" className="dock__manual-icon" />
          </button>
        </div>
      )}
      <NavBar current={tab} onSelect={onSelect} />
    </div>
  );
}
